import React from 'react';
import { useTranslation } from 'react-i18next';
import { LoadTile } from './LoadTile';
import { TRAVEL } from '../../../lib/rcu/catalog/protocol';
import type { RcuConfig, WiredLoad } from '../../../lib/rcu/model';
import type { RoomId } from '../../../lib/rcu/ids';
import type { LoadGroup } from '../../../lib/rcu/query/grouping';
import {
  describeCommand,
  setLevel as levelCommand,
  setTravel as travelCommand,
  toggleCommand,
} from '../../../lib/rcu/transport';
import type { RcuCommand } from '../../../lib/rcu/transport';
import { documentFor, refusalOutcome } from '../../../lib/rcu/link/dispatch';
import { observeLoad, resolveLoadState } from '../../../lib/rcu/link/observe';
import { channelSubtext } from '../../../lib/rcu/query/targets';
import { useLoadStateStore } from '../../../stores/loadStateStore';
import { useRcuLinkStore } from '../../../stores/rcuLinkStore';
import { useToastStore } from '../../../stores/toastStore';

/** Room tabs, reusing the guest device controls for each wired load. */
const RoomTabs: React.FC<{
  groups: readonly LoadGroup[];
  activeRoomId: RoomId | null;
  onSelect: (index: number) => void;
}> = ({ groups, activeRoomId, onSelect }) => {
  const { t } = useTranslation();

  const nameOf = (group: LoadGroup): string =>
    group.roomId === null ? t('roomControls.tabs.unassigned') : group.name;

  return (
    <div className="mb-5 flex gap-1 overflow-x-auto" role="tablist">
      {groups.map((group, index) => {
        const isActive = group.roomId === activeRoomId;
        return (
          <button
            key={group.roomId ?? 'unassigned'}
            type="button"
            role="tab"
            aria-selected={isActive}
            onClick={() => onSelect(index)}
            className={`shrink-0 rounded-full px-4 py-2 text-sm font-medium transition-colors ${
              isActive
                ? 'bg-[#D4A054] text-white'
                : 'bg-[#F5F5F5] text-[#666666] hover:bg-[#E0E0E0]'
            }`}
          >
            {nameOf(group)}
          </button>
        );
      })}
    </div>
  );
};

/**
 * One wired load, rendered with the control its load type asks for.
 *
 * A guest action moves the control at once and sends a command. The RCU's own
 * reports then take over — the link keeps the device's state, not a copy of what
 * this tablet last sent — but only for a reading that arrived after the guest
 * touched the control, so nothing snaps back to a value read before their tap.
 */
const LoadControl: React.FC<{ load: WiredLoad; config: RcuConfig }> = ({ load, config }) => {
  const runtime = useLoadStateStore((state) => state.stateOf(load.id));
  const setPower = useLoadStateStore((state) => state.setPower);
  const setLevel = useLoadStateStore((state) => state.setLevel);
  const live = useRcuLinkStore((state) => state.live);
  const send = useRcuLinkStore((state) => state.send);
  const report = useToastStore((state) => state.error);

  /**
   * The guest's command goes out on whichever transport the tablet is set to,
   * exactly like a technician's does; only the wording of a failure differs.
   */
  const dispatch = React.useCallback(
    (command: RcuCommand): void => {
      const document = documentFor(config, command);
      const carried = document.ok
        ? send(config, document.value)
        : Promise.resolve(refusalOutcome(document.issue));

      void carried.then((outcome) => {
        if (!outcome.ok) report(`${load.name}: ${describeCommand(command)} — ${outcome.detail}`);
      });
    },
    [config, load.name, send, report],
  );

  const settled = resolveLoadState(observeLoad(load, config, live), runtime);

  /**
   * The switch sends what it is showing: for a dimmer that is the level on
   * screen, which may be the RCU's own report rather than this tablet's guess.
   */
  const handleToggle = (): void => {
    const next = !settled.power;
    setPower(load.id, next);
    dispatch(toggleCommand(load, settled.level, next));
  };

  return (
    <LoadTile
      load={load}
      subtext={channelSubtext(config, load.channel)}
      power={settled.power}
      level={settled.level}
      onToggle={handleToggle}
      onLevel={(level) => {
        setLevel(load.id, level);
        // Touching the slider implies the guest wants light.
        dispatch(levelCommand(load, level, true));
      }}
      onTravel={(travel) => {
        // Opening and closing also flip the switch the guest sees.
        if (travel === TRAVEL.OPEN) setPower(load.id, true);
        if (travel === TRAVEL.CLOSE) setPower(load.id, false);
        dispatch(travelCommand(load, travel));
      }}
    />
  );
};

export interface ConfiguredControlsProps {
  groups: readonly LoadGroup[];
  /** The stored configuration: commands are resolved against it before sending. */
  config: RcuConfig;
  /** Room this tablet belongs to, preselected when present. */
  defaultRoomId: RoomId | null;
}

/** The guest control surface once a technician has wired loads. */
export const ConfiguredControls: React.FC<ConfiguredControlsProps> = ({
  groups,
  config,
  defaultRoomId,
}) => {
  const [selected, setSelected] = React.useState<number | null>(null);

  /**
   * The tab to open on: this tablet's room when it has loads, otherwise the
   * first one. Unassigned is a fallback, never a destination.
   */
  const openingIndex = (): number => {
    const index = groups.findIndex(
      (group) => group.roomId !== null && group.roomId === defaultRoomId,
    );
    return index === -1 ? 0 : index;
  };

  const activeIndex = selected ?? openingIndex();
  const active = groups[activeIndex] ?? groups[0];

  return (
    <div>
      <RoomTabs groups={groups} activeRoomId={active?.roomId ?? null} onSelect={setSelected} />

      <div className="grid grid-cols-1 gap-4 md:grid-cols-2 lg:grid-cols-3">
        {active?.loads.map((load) => (
          <LoadControl key={load.id} load={load} config={config} />
        ))}
      </div>
    </div>
  );
};

export default ConfiguredControls;
